import { MailOutlined, MobileOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { Col, Row, Tag } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import {
  Card,
  CardBody,
  CardHeader,
  ClipboardCopy,
  EmailLink,
  Flex,
  FormItem,
  FormText,
  Input,
} from 'ui';
import { withEllipsis } from 'ui/styles';
import { CHANGE_INDICATOR_EDIT } from 'utils/change-indicators';

const Text = styled(FormText)`
  display: flex;
  align-items: center;
  > span {
    margin-right: 5px;
  }
`;

const Name = styled.div`
  color: ${p => p.theme.colors.theme};
  font-weight: ${p => p.theme.fontWeight.bolder};
  font-size: ${p => p.theme.fontSize.normal};
  ${withEllipsis};
`;

const StyledCardHeader = styled(CardHeader)`
  gap: 5px;
`;

const FormItemNames = styled(Flex)`
  flex: 1;
  align-items: center;
  gap: 5px;
  padding-bottom: 10px;
  .ant-legacy-form-item {
    flex: 1;
    margin-bottom: 0 !important;
  }
`;

const StyledEmailLink = styled(EmailLink)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledInput = styled(Input)`
  height: 34px !important;
  input {
    height: auto;
  }
`;

const StyledTag = styled(Tag)`
  margin-right: 0px;
  margin-left: auto;
`;

const Contact = (props) => {
  const {
    contact: { firstName, lastName, phone, email, mobile, isDefault },
    isEditable,
    disabled,
    index,
    form,
    intl,
  } = props;

  const setChangeIndicator = () => {
    const { getFieldDecorator, setFieldsValue } = form;
    getFieldDecorator(`contactPeople[${index}].changeIndicator`);
    setFieldsValue({ [`contactPeople[${index}].changeIndicator`]: CHANGE_INDICATOR_EDIT });
  };

  if (isEditable) {
    const {
      form: { getFieldDecorator },
    } = props;
    const firstNameValidator = getFieldDecorator(`contactPeople[${index}].firstName`, {
      initialValue: firstName,
    });
    const lastNameValidator = getFieldDecorator(`contactPeople[${index}].lastName`, {
      initialValue: lastName,
      rules: [{ required: true, message: intl.formatMessage({ id: 'rules.LAST_NAME' }) }],
    });
    const phoneValidator = getFieldDecorator(`contactPeople[${index}].phone`, {
      initialValue: phone,
    });
    const mobileValidator = getFieldDecorator(`contactPeople[${index}].mobile`, {
      initialValue: mobile,
    });
    const emailValidator = getFieldDecorator(`contactPeople[${index}].email`, {
      initialValue: email,
    });
    return (
      <Card gutter={15} position={index + 1}>
        <StyledCardHeader>
          {
            <FormItemNames>
              <FormItem label={intl.formatMessage({ id: 'common.labels.FIRST_NAME' })}>
                {firstNameValidator(
                  <StyledInput
                    prefix={<UserOutlined />}
                    disabled={disabled}
                    onChange={setChangeIndicator}
                  />,
                )}
              </FormItem>
              <FormItem label={intl.formatMessage({ id: 'common.labels.LAST_NAME' })}>
                {lastNameValidator(<Input disabled={disabled} onChange={setChangeIndicator} />)}
              </FormItem>
            </FormItemNames>
          }
          {isDefault ?
            <StyledTag color='blue'>{intl.formatMessage({ id: 'partners.DEFAULT' })}</StyledTag>
            : null}
        </StyledCardHeader>
        <CardBody paddingBottom={10}>
          <Row type='flex' gutter={[0, 5]}>
            <Col span={24}>
              <FormItem>
                {phoneValidator(
                  <StyledInput
                    prefix={<PhoneOutlined />}
                    disabled={disabled}
                    onChange={setChangeIndicator}
                  />,
                )}
              </FormItem>
            </Col>
            <Col span={24}>
              <FormItem>
                {mobileValidator(
                  <StyledInput
                    prefix={<MobileOutlined />}
                    disabled={disabled}
                    onChange={setChangeIndicator}
                  />,
                )}
              </FormItem>
            </Col>
            <Col span={24}>
              <FormItem>
                {emailValidator(
                  <StyledInput
                    type='email'
                    prefix={<MailOutlined />}
                    disabled={disabled}
                    onChange={setChangeIndicator}
                  />,
                )}
              </FormItem>
            </Col>
          </Row>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card gutter={15} position={index + 1} data-test-id='contact-card'>
      <CardHeader>
        <Name>
          <UserOutlined /> {firstName} {lastName}
        </Name>
        {isDefault ?
          <StyledTag color='blue'>{intl.formatMessage({ id: 'partners.DEFAULT' })}</StyledTag>
          : null}
      </CardHeader>
      <CardBody>
        <Row type='flex' gutter={16}>
          <Col span={24}>
            <FormItem>
              <Text>
                <PhoneOutlined />
                {phone ?
                  <ClipboardCopy prefixWidth='25px'>{phone}</ClipboardCopy> :
                  <FormattedMessage id='contacts.NO_PHONE_TEXT' />
                }
              </Text>
            </FormItem>
          </Col>
        </Row>
        <Row type='flex' gutter={16}>
          <Col span={24}>
            <FormItem>
              <Text>
                <MobileOutlined />
                {mobile ?
                  <ClipboardCopy prefixWidth='25px'>{mobile}</ClipboardCopy> :
                  <FormattedMessage id='contacts.NO_MOBILE_TEXT' />
                }
              </Text>
            </FormItem>
          </Col>
        </Row>
        <Row type='flex' gutter={16}>
          <Col span={24}>
            <FormItem>
              <Text>
                <MailOutlined />
                {email ?
                  <ClipboardCopy onlyCopyThroughIcon={true} prefixWidth='25px'>
                    <StyledEmailLink href={`mailto:${email}`}>{email}</StyledEmailLink>
                  </ClipboardCopy> :
                  <FormattedMessage id='contacts.NO_EMAIL_TEXT' />
                }
              </Text>
            </FormItem>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

Contact.propTypes = {
  contact: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    phone: PropTypes.string,
    mobile: PropTypes.string,
    email: PropTypes.string,
    isDefault: PropTypes.string,
  }),
  isEditable: PropTypes.bool,
  form: PropTypes.object,
  disabled: PropTypes.bool,
  index: PropTypes.number,
  intl: PropTypes.object,
};

export default injectIntl(Contact);
